import * as React from "react"
import { Head, router } from "@inertiajs/react"
import { Check, Loader2, RefreshCw, Search } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"

type AccountOption = {
  id: number
  name: string
  code: string | null
  outlet_group_key: string
  is_key_account: boolean
  channel_group: string | null
}

type MappingRow = {
  id: number
  account_code: string
  source_name: string | null
  dc_name: string | null
  dq_reason: string | null
  resolved_by: string
  confidence: number | null
  account: AccountOption | null
}

type ConservationRow = {
  period_year: number
  period_month: number
  source_value: number
  fact_value: number
  delta: number
  within_tolerance: boolean
}

type Reconciliation = {
  coverage: {
    coverage_pct: number
    product_coverage_pct: number
    account_coverage_pct: number
    total_value: number
    resolved_value: number
    quarantined_value: number
    unresolved_accounts: number
    unresolved_products: number
  }
  conservation: {
    within_tolerance: boolean
    total_source_value: number
    total_fact_value: number
    total_delta: number
    rows: ConservationRow[]
  }
}

type Props = {
  mappings: MappingRow[]
  filters: { view: string; dq_reason: string | null }
  filter_options: { dq_reasons: string[] }
  counts: { unresolved: number; unmatched: number; ambiguous: number; human_resolved: number }
  reconciliation: Reconciliation
}

const PATH = "/admin/sell-in/crosswalk"
const DESCRIPTION =
  "Crosswalk akun B2B (account_code) ke dimensi akun kanonik + readout konsistensi & cakupan fact_sell_in. Selesaikan kode yang belum tertaut agar sell-in tersambung ke directive, signal, dan target."

const DQ_LABELS: Record<string, string> = { unmatched: "Tak ada kandidat", ambiguous: "Ambigu (>1)" }
const MONTHS = ["", "Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"]

const isNum = (v: unknown): v is number => typeof v === "number" && !Number.isNaN(v)
function fmtPct(v: number | null | undefined, digits = 1): string {
  return isNum(v) ? `${v.toFixed(digits)}%` : "—"
}
function fmtIdr(v: number | null | undefined): string {
  if (!isNum(v)) return "—"
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(v)
}

function DqBadge({ reason }: { reason: string | null }) {
  if (!reason) return null
  return reason === "ambiguous" ? (
    <Badge tone="signal">{DQ_LABELS.ambiguous}</Badge>
  ) : (
    <Badge tone="muted">{DQ_LABELS.unmatched}</Badge>
  )
}

function pushQuery(next: Record<string, string | null | undefined>) {
  const q: Record<string, string> = {}
  Object.entries(next).forEach(([k, v]) => {
    if (v != null && v !== "") q[k] = String(v)
  })
  router.get(PATH, q, { preserveState: true, preserveScroll: true, replace: true })
}

function StatCard({ label, value, tone }: { label: string; value: string; tone?: "ok" | "warn" }) {
  return (
    <div className="rounded-lg border border-hairline bg-surface p-4">
      <div className="text-xs text-ink-muted">{label}</div>
      <div
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          tone === "warn" ? "text-amber-600 dark:text-amber-400" : "text-ink-body"
        }`}
      >
        {value}
      </div>
    </div>
  )
}

// The resolve picker: a server-backed typeahead over the canonical accounts. The
// fetch runs only in effects/handlers (never during SSR/render).
function ResolveDialog({ mapping }: { mapping: MappingRow }) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState(mapping.source_name ?? mapping.account_code)
  const [results, setResults] = React.useState<AccountOption[]>([])
  const [loading, setLoading] = React.useState(false)
  const [selected, setSelected] = React.useState<AccountOption | null>(mapping.account)
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (!open) return
    const term = query.trim()
    if (term.length < 2) {
      setResults([])
      return
    }
    setLoading(true)
    const controller = new AbortController()
    const timer = setTimeout(() => {
      fetch(`${PATH}/account-search?q=${encodeURIComponent(term)}`, {
        headers: { Accept: "application/json" },
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((data) => setResults(data.results ?? []))
        .catch(() => {
          /* aborted or network — ignore */
        })
        .finally(() => setLoading(false))
    }, 250)
    return () => {
      controller.abort()
      clearTimeout(timer)
    }
  }, [open, query])

  const submit = () => {
    if (!selected) return
    setSubmitting(true)
    router.patch(
      `${PATH}/${mapping.id}`,
      { account_id: selected.id },
      {
        preserveScroll: true,
        onSuccess: () => setOpen(false),
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <>
      <Button size="sm" variant={mapping.account ? "secondary" : "primary"} onClick={() => setOpen(true)}>
        {mapping.account ? "Ubah" : "Selesaikan"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Tautkan akun</DialogTitle>
            <DialogDescription>
              Tautkan kode <strong>{mapping.account_code}</strong>
              {mapping.source_name ? ` (${mapping.source_name})` : ""} ke akun kanonik yang benar.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label htmlFor="acct-q">Cari akun</label>
              <div className="relative mt-1">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted"
                  aria-hidden="true"
                />
                <Input
                  id="acct-q"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nama akun atau kode"
                  className="pl-9"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-lg border border-hairline">
              {loading ? (
                <div className="flex items-center justify-center gap-2 p-6 text-sm text-ink-muted">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Mencari…
                </div>
              ) : results.length === 0 ? (
                <div className="p-6 text-center text-sm text-ink-muted">
                  {query.trim().length < 2 ? "Ketik minimal 2 karakter." : "Tidak ada akun yang cocok."}
                </div>
              ) : (
                <ul>
                  {results.map((acct) => {
                    const active = selected?.id === acct.id
                    return (
                      <li key={acct.id}>
                        <button
                          type="button"
                          onClick={() => setSelected(acct)}
                          className={`flex w-full items-start justify-between gap-3 border-b border-hairline px-4 py-3 text-left last:border-b-0 hover:bg-surface ${
                            active ? "bg-surface" : ""
                          }`}
                          aria-pressed={active}
                        >
                          <span className="min-w-0">
                            <span className="block font-medium text-ink-body">{acct.name}</span>
                            <span className="block text-xs text-ink-muted">
                              {acct.outlet_group_key}
                              {acct.code ? ` · ${acct.code}` : ""}
                              {acct.channel_group ? ` · ${acct.channel_group}` : ""}
                            </span>
                          </span>
                          <span className="flex shrink-0 items-center gap-2">
                            {acct.is_key_account && <Badge tone="accent">KA</Badge>}
                            {active && <Check className="h-4 w-4 text-accent" aria-hidden="true" />}
                          </span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>

            {selected && (
              <p className="text-sm text-ink-muted">
                Akan ditautkan ke: <strong className="text-ink-body">{selected.name}</strong>.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Batal
            </Button>
            <Button onClick={submit} disabled={!selected || submitting}>
              {submitting ? "Menyimpan…" : "Tautkan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default function Index({ mappings, filters, filter_options, counts, reconciliation }: Props) {
  const resolvedView = filters.view === "resolved"
  const [rebuilding, setRebuilding] = React.useState(false)
  const { coverage, conservation } = reconciliation

  const rebuild = () => {
    setRebuilding(true)
    router.post(`${PATH}/rebuild`, {}, { preserveScroll: true, onFinish: () => setRebuilding(false) })
  }

  return (
    <>
      <Head title="Sell-In Crosswalk">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Sell-In Crosswalk" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell width="wide">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-hairline pb-6">
          <div>
            <h1>Sell-In Crosswalk</h1>
            <p className="mt-1 max-w-3xl text-sm text-ink-muted">{DESCRIPTION}</p>
          </div>
          <Button variant="secondary" onClick={rebuild} disabled={rebuilding}>
            <RefreshCw className={`mr-2 h-4 w-4 ${rebuilding ? "animate-spin" : ""}`} aria-hidden="true" />
            {rebuilding ? "Menjalankan…" : "Rebuild"}
          </Button>
        </div>

        {/* Coverage + conservation readout */}
        <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <StatCard
            label="Cakupan kanonik"
            value={fmtPct(coverage.coverage_pct)}
            tone={coverage.coverage_pct < 100 ? "warn" : "ok"}
          />
          <StatCard label="Cakupan akun" value={fmtPct(coverage.account_coverage_pct)} />
          <StatCard label="Cakupan produk" value={fmtPct(coverage.product_coverage_pct)} />
          <StatCard
            label="Nilai terkarantina"
            value={fmtIdr(coverage.quarantined_value)}
            tone={coverage.quarantined_value > 0 ? "warn" : "ok"}
          />
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2 rounded-lg border border-hairline bg-surface p-4 text-sm">
          <span className="flex items-center gap-2">
            <span className="text-ink-muted">Konsistensi (sumber vs fact):</span>
            {conservation.within_tolerance ? (
              <Badge tone="success">Cocok dalam toleransi</Badge>
            ) : (
              <Badge tone="danger">Selisih di luar toleransi</Badge>
            )}
          </span>
          <span className="text-ink-muted">
            Sumber <strong className="text-ink-body tabular-nums">{fmtIdr(conservation.total_source_value)}</strong> ·
            fact <strong className="text-ink-body tabular-nums">{fmtIdr(conservation.total_fact_value)}</strong> · Δ{" "}
            <strong className="text-ink-body tabular-nums">{fmtIdr(conservation.total_delta)}</strong>
          </span>
        </div>

        {/* DQ banner */}
        {counts.unresolved > 0 && (
          <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200">
            <strong>{counts.unresolved} kode akun</strong> belum tertaut ({counts.unmatched} tak ada kandidat,{" "}
            {counts.ambiguous} ambigu). Sell-in dari kode ini terkarantina (nilai tetap dihitung, tetapi belum
            tersambung ke akun) sampai diselesaikan di bawah.
          </div>
        )}

        {/* View toggle */}
        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            variant={resolvedView ? "secondary" : "primary"}
            size="sm"
            onClick={() => pushQuery({})}
          >
            Belum terselesaikan ({counts.unresolved})
          </Button>
          <Button
            variant={resolvedView ? "primary" : "secondary"}
            size="sm"
            onClick={() => pushQuery({ view: "resolved" })}
          >
            Selesai manual ({counts.human_resolved})
          </Button>
          {!resolvedView && (
            <div className="ml-auto min-w-[180px]">
              <Select
                aria-label="Filter alasan DQ"
                value={filters.dq_reason ?? ""}
                onChange={(e) => pushQuery({ dq_reason: e.target.value })}
              >
                <option value="">Semua alasan</option>
                {filter_options.dq_reasons.map((r) => (
                  <option key={r} value={r}>
                    {DQ_LABELS[r] ?? r}
                  </option>
                ))}
              </Select>
            </div>
          )}
        </div>

        {/* Crosswalk queue */}
        {mappings.length === 0 ? (
          <div className="mt-6 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">
              {resolvedView
                ? "Belum ada kode yang diselesaikan manual."
                : counts.unresolved === 0
                  ? "Semua kode akun tertaut. 🎉"
                  : "Tidak ada item untuk filter ini."}
            </p>
          </div>
        ) : (
          <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-ink-muted">
                <tr>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Kode
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    Nama sumber
                  </th>
                  <th scope="col" className="px-4 py-3 font-medium">
                    {resolvedView ? "Ditautkan ke" : "Alasan"}
                  </th>
                  <th scope="col" className="px-4 py-3 text-right font-medium">
                    Aksi
                  </th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((m) => (
                  <tr key={m.id} className="border-t border-hairline">
                    <td className="px-4 py-3 font-medium text-ink-body">{m.account_code}</td>
                    <td className="px-4 py-3 text-ink-muted">{m.source_name ?? "—"}</td>
                    <td className="px-4 py-3">
                      {resolvedView ? (
                        m.account ? (
                          <span className="text-ink-body">{m.account.name}</span>
                        ) : (
                          <span className="text-ink-muted">—</span>
                        )
                      ) : (
                        <DqBadge reason={m.dq_reason} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ResolveDialog mapping={m} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Per-period conservation detail */}
        {conservation.rows.length > 0 && (
          <details className="mt-6 rounded-lg border border-hairline">
            <summary className="cursor-pointer px-4 py-3 text-sm font-medium text-ink-body">
              Rincian konsistensi per bulan ({conservation.rows.length})
            </summary>
            <div className="overflow-x-auto border-t border-hairline">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th scope="col" className="px-4 py-3 font-medium">
                      Periode
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-medium">
                      Sumber
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-medium">
                      Fact
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-medium">
                      Δ
                    </th>
                    <th scope="col" className="px-4 py-3 text-right font-medium">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {conservation.rows.map((r) => (
                    <tr key={`${r.period_year}-${r.period_month}`} className="border-t border-hairline">
                      <td className="px-4 py-3 text-ink-body">
                        {MONTHS[r.period_month] ?? r.period_month} {r.period_year}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-ink-body">{fmtIdr(r.source_value)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-ink-body">{fmtIdr(r.fact_value)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-ink-muted">{fmtIdr(r.delta)}</td>
                      <td className="px-4 py-3 text-right">
                        {r.within_tolerance ? (
                          <Badge tone="success">OK</Badge>
                        ) : (
                          <Badge tone="danger">Selisih</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </AppShell>
    </>
  )
}
